/**
 * Feedback API utilities
 * Handles communication with the feedback submission endpoint
 */

// Note: We'll get the auth token from the useAuth hook context

export interface FeedbackSubmissionData {
  title: string;
  category: 'issue' | 'idea' | 'question' | 'complaint' | 'feature-request' | 'other';
  message: string;
  imageUrls?: string[];
}

export interface FeedbackApiResponse {
  success: boolean;
  message?: string;
  error?: {
    type: string;
    message: string;
    code: string;
    details?: Array<{
      field: string;
      message: string;
      code: string;
    }>;
  };
}

export class FeedbackApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public type: string,
    public details?: Array<{ field: string; message: string; code: string }>
  ) {
    super(message);
    this.name = 'FeedbackApiError';
  }
}

/**
 * Submit feedback to the API
 */
export async function submitFeedback(
  data: FeedbackSubmissionData, 
  getAccessToken: () => Promise<string>
): Promise<FeedbackApiResponse> {
  let token: string;
  
  try {
    token = await getAccessToken();
  } catch (error) {
    throw new FeedbackApiError(
      'Authentication required to submit feedback',
      'AUTH_REQUIRED',
      'AUTHENTICATION_ERROR'
    );
  }

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';
  const url = API_BASE_URL ? `${API_BASE_URL}/api/feedback/submit` : '/api/feedback/submit';
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const responseData: FeedbackApiResponse = await response.json();

  if (!response.ok) {
    const error = responseData.error;
    if (error) {
      throw new FeedbackApiError(
        error.message,
        error.code,
        error.type,
        error.details
      );
    } else {
      throw new FeedbackApiError(
        'Failed to submit feedback',
        'UNKNOWN_ERROR',
        'SYSTEM_ERROR'
      );
    }
  }

  return responseData;
}

/**
 * Validate feedback data on the client side
 */
export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export function validateFeedbackData(data: Partial<FeedbackSubmissionData>): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate title
  if (!data.title || !data.title.trim()) {
    errors.push({
      field: 'title',
      message: 'Title is required',
      code: 'REQUIRED_FIELD'
    });
  } else if (data.title.length > 200) {
    errors.push({
      field: 'title',
      message: 'Title must be less than 200 characters',
      code: 'TITLE_TOO_LONG'
    });
  }

  // Validate category
  if (!data.category) {
    errors.push({
      field: 'category',
      message: 'Category is required',
      code: 'REQUIRED_FIELD'
    });
  } else {
    const validCategories = ['issue', 'idea', 'question', 'complaint', 'feature-request', 'other'];
    if (!validCategories.includes(data.category)) {
      errors.push({
        field: 'category',
        message: 'Invalid category selected',
        code: 'INVALID_CATEGORY'
      });
    }
  }

  // Validate message
  if (!data.message || !data.message.trim()) {
    errors.push({
      field: 'message',
      message: 'Message is required',
      code: 'REQUIRED_FIELD'
    });
  } else if (data.message.length > 2000) {
    errors.push({
      field: 'message',
      message: 'Message must be less than 2000 characters',
      code: 'MESSAGE_TOO_LONG'
    });
  }

  // Validate imageUrls (optional field)
  if (data.imageUrls && data.imageUrls.length > 0) {
    if (data.imageUrls.length > 3) {
      errors.push({
        field: 'imageUrls',
        message: 'Maximum 3 images allowed',
        code: 'TOO_MANY_IMAGES'
      });
    }

    // Validate that URLs are valid Supabase Storage URLs
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    if (supabaseUrl) {
      const invalidUrls = data.imageUrls.filter(url =>
        !url.startsWith(`${supabaseUrl}/storage/v1/object/public/feedback-attachments/`)
      );

      if (invalidUrls.length > 0) {
        errors.push({
          field: 'imageUrls',
          message: 'Invalid image URLs detected',
          code: 'INVALID_IMAGE_URLS'
        });
      }
    }
  }

  return errors;
}

/**
 * Get user-friendly error message for API errors
 */
export function getFriendlyErrorMessage(error: FeedbackApiError): string {
  switch (error.code) {
    case 'AUTH_REQUIRED':
    case 'INVALID_TOKEN':
      return 'Please log in to submit feedback.';
    
    case 'MISSING_REQUIRED_FIELDS':
      return 'Please fill in all required fields.';
    
    case 'INVALID_CATEGORY':
      return 'Please select a valid feedback category.';
    
    case 'TITLE_TOO_LONG':
      return 'Title is too long. Please keep it under 200 characters.';
    
    case 'MESSAGE_TOO_LONG':
      return 'Message is too long. Please keep it under 2000 characters.';

    case 'TOO_MANY_IMAGES':
      return 'You can attach a maximum of 3 images to your feedback.';

    case 'INVALID_IMAGE_URLS':
      return 'One or more image URLs are invalid. Please re-upload your images.';

    case 'EMAIL_SEND_FAILED':
      return 'Your feedback was received but we couldn\'t send the notification. We\'ll still review your feedback.';
    
    case 'FEEDBACK_SUBMISSION_ERROR':
    case 'SYSTEM_ERROR':
      return 'Something went wrong. Please try again or contact support if the problem persists.';
    
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Retry function with exponential backoff
 */
export async function retrySubmitFeedback(
  data: FeedbackSubmissionData,
  getAccessToken: () => Promise<string>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<FeedbackApiResponse> {
  let lastError: FeedbackApiError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await submitFeedback(data, getAccessToken);
    } catch (error) {
      lastError = error instanceof FeedbackApiError ? error : new FeedbackApiError(
        'Unknown error occurred',
        'UNKNOWN_ERROR',
        'SYSTEM_ERROR'
      );

      // Don't retry for validation errors or auth errors
      if (lastError.type === 'VALIDATION_ERROR' || lastError.type === 'AUTHENTICATION_ERROR') {
        throw lastError;
      }

      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Wait before retrying with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}